<template>
  <div 
    v-if="visible" 
    class="chat-window" 
    :style="{ width: chatWidth + 'px', height: chatHeight + 'px' }"
  >
    <!-- 🔹 Manipulador de Redimensionamento -->
    <div class="resize-handle" @mousedown="startResize"></div>

    <!-- Cabeçalho do Chat -->
    <div class="chat-header">
      <h3>Conversa com {{ contactName }}</h3>
      <button class="close-button" @click="closeChat">&times;</button>
    </div>

    <div class="typing-strip" v-if="isTyping || isCustomerTyping">
      <span v-if="isTyping">digitando…</span>
      <span v-else-if="isCustomerTyping">{{ contactName || 'Contato' }} está digitando…</span>
    </div>

    <!-- Histórico de mensagens -->
    <div class="chat-messages" ref="messagesContainer">
      <template v-if="isLoadingMessages">
        <div
          v-for="n in 6"
          :key="n"
          class="chat-skeleton-row"
          :class="n % 2 === 0 ? 'left' : 'right'"
          aria-hidden="true"
        >
          <div class="chat-skeleton-bubble shimmer">
            <div class="skeleton-line" style="width: 180px; height: 12px;"></div>
            <div class="skeleton-line" style="width: 120px; height: 12px; margin-top: 6px;"></div>
          </div>
        </div>
      </template>

      <template v-else>
          <div
            v-for="(message, index) in messages"
            :key="message.msgId || message._localId || index"
            :class="['message', message.type]"
          >
          <div class="message-text">{{ message.text }}</div>
          <div class="message-meta">
            <span class="timestamp">{{ message.timestamp }}</span>
            <span
              v-if="message.type === 'sent'"
              class="ack"
              :class="'ack-' + (message.ack ?? 0)"
              :title="ackTitle(message.ack)"
            >
              {{ ackGlyph(message.ack) }}
            </span>
          </div>
        </div>
      </template>
    </div>

    <!-- Campo de entrada -->
    <div class="chat-input">
      <input
        v-model="newMessage"
        type="text"
        placeholder="Digite sua mensagem..."
        @keyup.enter="sendMessage"
        :disabled="isSending"
      />
      <button @click="sendMessage" :disabled="isSending">
        {{ isSending ? "Enviando..." : "Enviar" }}
      </button>
    </div>
  </div>
</template>

<script>
import { secureFetch } from "@/main";
import { getAuth } from "firebase/auth";
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";

export default {
  props: {
    phoneNumber: String,
    contactName: String,
    visible: Boolean,
    socket: Object,
  },
  data() {
    return {
      messages: [],
      newMessage: "",
      chatWidth: 350,
      chatHeight: 500,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      isSending: false,
      isResizing: false,
      isTyping: false,
      isCustomerTyping: false,
      isLoadingMessages: false,
      fetchSeq: 0,
      pendingAcks: {},
      //typingTimer: null,
      //stopTypingTimer: null,
      lastIncomingSig: null,
      lastIncomingAt: 0,
    };
  },
  mounted() {
    const sock = this.socket;
    if (!sock) return;

    //const norm = (p) => String(p || "").replace(/\D/g, "");

    if (sock) {
      const norm = (p) => String(p || "").replace(/\D/g, "");

      sock.on("customer-typing", ({ phone }) => {
        if (norm(phone) === norm(this.phoneNumber)) {
          this.isCustomerTyping = true;
          setTimeout(() => (this.isCustomerTyping = false), 3000);
        }
      });

      const handleAckUpdate = (payload) => {
        console.log("📩 [socket] message-status-update:", payload);

        const norm = (p) => String(p || "").replace(/\D/g, "");

        const phone = payload?.phone;
        const finalAck =
          typeof payload?.status === "number" ? payload.status : payload.ack;

        const messageId = payload?.msgId || payload?.id || null;

        if (finalAck == null) return;

        // ✅ 0) NORMALIZA: texto nunca deve mostrar "played"
        const ackUi = this.normalizeAckForUi
          ? this.normalizeAckForUi(finalAck)
          : (finalAck >= 4 ? 3 : finalAck);

        // ✅ 1) PRIORIDADE: se tiver msgId, atualiza por msgId (independente do phone)
        if (messageId) {
          for (let i = this.messages.length - 1; i >= 0; i--) {
            const m = this.messages[i];
            if (m.type === "sent" && m.msgId === messageId) {
              this.messages.splice(i, 1, { ...m, ack: ackUi });
              return;
            }
          }

          // ✅ NÃO ACHOU A MSG AINDA: guarda pra aplicar depois
          this.pendingAcks[messageId] = ackUi;

          // (opcional) força re-render (ajuda em alguns casos)
          this.$forceUpdate?.();
          return;
        }

        // ✅ 2) Só depois valida o phone pra fallback de "última enviada"
        if (norm(phone) !== norm(this.phoneNumber)) return;

        for (let i = this.messages.length - 1; i >= 0; i--) {
          const m = this.messages[i];
          if (m.type === "sent") {
            this.messages.splice(i, 1, { ...m, ack: ackUi });
            return;
          }
        }
      };

      sock.on("message-ack", handleAckUpdate);
      sock.on("message-status-update", handleAckUpdate);

      // (opcional) guarde referência pra remover certinho
      this._handleAckUpdate = handleAckUpdate;
    }
  },
  beforeUnmount() {
    const sock = this.socket;
    if (sock) {
      
      sock.off("customer-typing");
      if (this._handleAckUpdate) {
        sock.off("message-ack", this._handleAckUpdate);
        sock.off("message-status-update", this._handleAckUpdate);
      } else {
        sock.off("message-ack");
        sock.off("message-status-update");
      }
    }
  },
  watch: {
    phoneNumber: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;

        // ✅ “zera” a UI imediatamente ao trocar de card
        this.isLoadingMessages = true;
        this.messages = [];

        // ✅ dispara o fetch com token
        this.fetchMessages();
      },
    },
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom(true);
          setTimeout(() => this.scrollToBottom(true), 50);
        });
      }
    },
  },
  methods: {
    normalizeAckForUi(inAck) {
      let ack = Number(inAck);
      if (!Number.isFinite(ack)) return null;

      if (ack >= 4) ack = 3;

      if (ack < 0) ack = 0;
      if (ack > 3) ack = 3;
      return ack;
    },
    async resolveUserEmail() {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user?.email) return user.email;

      return (
        localStorage.getItem("email_ini") ||
        localStorage.getItem("ssoEmail") ||
        null
      );
    },
    ackGlyph(ack) {
      switch (ack) {
        case 1: return "✔"; // enviado ao servidor
        case 2: return "✔✔"; // entregue
        case 3: return "✔✔"; // lido
        case 4: return "▶︎✔✔"; // reproduzido (áudio)
        default: return "⌛"; // pendente
      }
    },
    ackTitle(ack) {
      return (
        {
          0: "Pendente",
          1: "Enviado ao servidor",
          2: "Entregue",
          3: "Lido",
          4: "Reproduzido",
        }[ack ?? 0] || "Pendente"
      );
    },

    async fetchMessages() {
      if (!this.phoneNumber) return;

      const seq = ++this.fetchSeq;
      this.isLoadingMessages = true;

      try {
        const email = await this.resolveUserEmail();

        if (!email) {
          console.warn("⚠️ Nenhum e-mail (Firebase ou SSO) ao carregar mensagens.");
          return;
        }

        const resp = await secureFetch(
          `${apiBaseUrl}/get-messages?telefoneCliente=${encodeURIComponent(this.phoneNumber)}&userEmail=${encodeURIComponent(email)}`,
          { method: "GET" }
        );

        if (!resp || !resp.ok) {
          console.error("❌ Erro ao buscar mensagens. Status:", resp?.status);
          return;
        }

        const data = await resp.json();

        const parsed = (Array.isArray(data) ? data : [])
          .map((msg, index) => {
            const raw =
              msg.text ??
              msg.caption ??
              msg.mensagem ??
              msg.message ??
              msg.body ??
              "";
            const text =
              typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();

            const fromVendor =
              msg.from === "vendedor" ||
              msg.origin === "vendedor" ||
              msg.tipoMsg === "Vendedor" ||
              msg.type === "sent";

            let dateObj;
            if (msg.time) {
              const numeric = Number(msg.time);
              if (!isNaN(numeric)) {
                dateObj = new Date(numeric);
              } else if (typeof msg.time === "string" && msg.time.includes("/")) {
                const [datePart, timePart] = msg.time.split(" ");
                if (datePart && timePart) {
                  const [d, m, y] = datePart.split("/").map(Number);
                  const [h, min] = timePart.split(":").map(Number);
                  dateObj = new Date(y, m - 1, d, h, min);
                }
              } else {
                dateObj = new Date(msg.time);
              }
            }
            if (!dateObj || isNaN(dateObj.getTime())) dateObj = new Date();

            const timestamp = `${String(dateObj.getDate()).padStart(2, "0")}/${String(
              dateObj.getMonth() + 1
            ).padStart(2, "0")} ${String(dateObj.getHours()).padStart(2, "0")}:${String(
              dateObj.getMinutes()
            ).padStart(2, "0")}`;

            return {
              text,
              type: fromVendor ? "sent" : "received",
              timestamp,
              ack: msg.ack ?? (fromVendor ? 2 : null),
              msgId: msg.msgId ?? msg.id ?? msg.key?.id ?? null,
              _localId: msg.msgId ?? msg.id ?? `hist_${index}_${Date.now()}`,
            };
          })
          .filter((m) => m.text && m.text.length > 0);

        // ✅ Se o usuário já trocou de card enquanto carregava, IGNORA
        if (seq !== this.fetchSeq) return;
        if (this.phoneNumber == null) return;

        this.messages = parsed;
        for (let i = 0; i < this.messages.length; i++) {
          const mid = this.messages[i].msgId;
          if (mid && this.pendingAcks[mid] != null) {
            this.messages[i].ack = this.pendingAcks[mid];
            delete this.pendingAcks[mid];
          }
        }
        this.$nextTick(() => {
          this.scrollToBottom(true);
          setTimeout(() => this.scrollToBottom(true), 50);
        });
      } catch (error) {
        console.error("❌ Erro ao buscar mensagens:", error);
      } finally {
        // ✅ Só desliga loading se ainda for a requisição atual
        if (seq === this.fetchSeq) this.isLoadingMessages = false;
      }
    },

    async sendMessage() {
      const textToSend = (this.newMessage || "").trim();
      if (!textToSend || this.isSending) return;

      this.isSending = true;

      try {
        const email = await this.resolveUserEmail();
        if (!email) {
          console.warn("⚠️ Sem email para envio no chat.");
          return;
        }

        const messagesToSend = [{ number: this.phoneNumber, message: textToSend }];

        const formData = new FormData();
        formData.append("messages", JSON.stringify(messagesToSend));
        formData.append("mode", "chat");
        formData.append("email", email);

        const resp = await secureFetch(`${apiBaseUrl}/send-messages`, {
          method: "POST",
          body: formData,
        });

        if (!resp || !resp.ok) {
          console.error("❌ Erro ao enviar mensagem. Status:", resp?.status);
          return;
        }

        const data = await resp.json();
        const localId = `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;

        const first = data?.responses?.[0] || data?.response?.responses?.[0];
        
        const serverMsgId = first?.msgId || null;
        const pending = serverMsgId ? this.pendingAcks[serverMsgId] : null;
        const safePending = pending === 2 ? 2 : null;

        if (data?.success === true && first?.success !== false) {
          
          this.messages.push({
            _localId: localId,
            msgId: serverMsgId,
            text: textToSend,
            type: "sent",
            timestamp: new Date().toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            ack: safePending ?? 1,
          });

          if (serverMsgId && pending != null) {
            delete this.pendingAcks[serverMsgId];
          }

          // ✅ MUITO IMPORTANTE: atualiza o Kanban em tempo real
          this.$emit("update-last-message", {
            phone: this.phoneNumber,
            message: textToSend,
            // opcional (se quiser evoluir depois):
            msgId: first?.msgId || null,
            ack: 1,
          });

          this.newMessage = "";
          this.$nextTick(() => this.scrollToBottom());
        } else {
          console.warn("⚠️ Falha no envio:", first?.error || data);
        }
      } catch (e) {
        console.error("Erro ao enviar:", e);
      } finally {
        this.isSending = false;
      }
    },

    receiveMessageFromWebSocket(data) {
      const norm = (p) => String(p || "").replace(/\D/g, "");
      if (norm(this.phoneNumber) !== norm(data?.phone)) return;

      const txt = String(data?.message ?? "").trim();
      if (!txt) return;

      const now = Date.now();
      const sig = `${norm(data?.phone)}|${txt}`;

      // evita duplicata colada (replay/duplo emit)
      if (this.lastIncomingSig === sig && (now - this.lastIncomingAt) < 800) {
        return;
      }
      this.lastIncomingSig = sig;
      this.lastIncomingAt = now;

      this.messages.push({
        text: txt,
        type: "received",
        timestamp: new Date().toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });

      this.$nextTick(() => this.scrollToBottom(false));
    },

    startResize(event) {
      this.isResizing = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startWidth = this.chatWidth;
      this.startHeight = this.chatHeight;

      document.addEventListener("mousemove", this.resizeChat);
      document.addEventListener("mouseup", this.stopResize);
    },
    resizeChat(event) {
      if (!this.isResizing) return;
      const widthDiff = this.startX - event.clientX;
      const heightDiff = this.startY - event.clientY;
      this.chatWidth = Math.max(300, this.startWidth + widthDiff);
      this.chatHeight = Math.max(400, this.startHeight + heightDiff);
    },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.resizeChat);
      document.removeEventListener("mouseup", this.stopResize);
    },
    scrollToBottom(force = false) {
      const el = this.$refs.messagesContainer;
      if (!el) return;

      if (!force) {
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (distanceFromBottom > 120) return;
      }

      // força ir pro final
      el.scrollTop = el.scrollHeight;
    },
    closeChat() {
      this.$emit("close");
    },
  },
};
</script>
  
<style src="@/assets/styleChatWindow.css"></style>
  